import { motion } from "framer-motion";
import { User, Mail } from "lucide-react";
import InputField from "../../../shared/components/form/InputField";
import PhoneField from "../../../shared/components/form/PhoneField";
import PasswordField from "./PasswordField";
import type { TalentFormData, TalentFormErrors } from "../types/register-talent.types";
import { validateField } from "../utils/register-talent.utils";
import { getExistsFieldError } from "../utils/validators";
import { useLanguage } from "../../../context/LanguageContext";

interface Props {
  formData: TalentFormData;
  formErrors: TalentFormErrors;
  phoneExists?: boolean;
  checkingPhone?: boolean;
  emailExists?: boolean;
  checkingEmail?: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  onPhoneChange: (value: string) => void;
  onPhoneCountryChange: (country?: string) => void;
  onBlur: (field: keyof TalentFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const STEP1_FIELDS: (keyof TalentFormData)[] = [
  "first_name", "last_name", "email", "phone", "password", "confirmPassword",
];

export default function BasicInfoStep({
  formData, formErrors, phoneExists, checkingPhone, emailExists, checkingEmail,
  onChange, onPhoneChange, onPhoneCountryChange, onBlur, onSubmit,
}: Props) {
  const { t } = useLanguage();
  const isValid =
    STEP1_FIELDS.every((f) => !validateField(f, formData[f], formData)) &&
    !phoneExists && !emailExists;

  const emailError = getExistsFieldError(formErrors.email, checkingEmail, emailExists, t("auth.emailAlreadyRegistered"));

  return (
    <motion.form initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" onSubmit={onSubmit}>
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{t("auth.basicInfoStep.title")}</h3>
        <p className="text-sm text-gray-500">{t("auth.basicInfoStep.subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <InputField label={t("auth.firstName")} icon={User} required name="first_name"
          value={formData.first_name} onChange={onChange} onBlur={() => onBlur("first_name")}
          error={formErrors.first_name} placeholder={t("auth.firstNamePlaceholder")} />
        <InputField label={t("auth.lastName")} icon={User} required name="last_name"
          value={formData.last_name} onChange={onChange} onBlur={() => onBlur("last_name")}
          error={formErrors.last_name} placeholder={t("auth.lastNamePlaceholder")} />
      </div>
      <InputField label={t("auth.emailAddress")} icon={Mail} required type="email" name="email"
        value={formData.email} onChange={onChange} onBlur={() => onBlur("email")}
        error={emailError} placeholder={t("auth.emailPlaceholder")} />
      {checkingEmail && <p className="text-xs text-gray-400 -mt-3">{t("auth.checkingEmail")}</p>}
      <PhoneField required value={formData.phone} onChange={onPhoneChange} onCountryChange={onPhoneCountryChange}
        error={formErrors.phone} phoneExists={phoneExists} checkingPhone={checkingPhone} />
      <PasswordField label={t("auth.password")} name="password" value={formData.password}
        onChange={onChange as any} onBlur={() => onBlur("password")}
        error={formErrors.password} placeholder={t("auth.passwordPlaceholder")} />
      <PasswordField label={t("auth.confirmPassword")} name="confirmPassword" value={formData.confirmPassword}
        onChange={onChange as any} onBlur={() => onBlur("confirmPassword")}
        error={formErrors.confirmPassword} placeholder={t("auth.confirmPasswordPlaceholder")} />

      <button type="submit" disabled={!isValid || checkingPhone || checkingEmail}
        className="w-full bg-gradient-to-r from-[#b3d130] to-[#a5c428] text-gray-900 py-4 rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed">
        {t("auth.basicInfoStep.continueButton")}
      </button>
    </motion.form>
  );
}
